import { http } from '@/api/http';
import type { Schemas } from '@/api/shared';

export type IdentifierInUseResponseDto = Schemas['IdentifierInUseResponse'];

export const identifiersApi = {
  /** POST /persons/{guid}/identifiers — add a single identifier. */
  addIdentifier: (
    guid: string,
    body: Schemas['PersonIdentifier'],
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['PersonIdentifier']> =>
    http.post<Schemas['PersonIdentifier']>(
      `/persons/${encodeURIComponent(guid)}/identifiers`,
      body,
      options,
    ),

  /** PUT /persons/{guid}/identifiers — replace the entire identifier list. */
  replaceIdentifiers: (
    guid: string,
    body: Schemas['PersonIdentifier'][],
    options?: { signal?: AbortSignal },
  ): Promise<{ success?: boolean }> =>
    http.put<{ success?: boolean }>(
      `/persons/${encodeURIComponent(guid)}/identifiers`,
      body,
      options,
    ),

  /** DELETE /persons/{guid}/identifiers/{id} — remove a single identifier. */
  deleteIdentifier: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/identifiers/${encodeURIComponent(id)}`,
      options,
    ),

  /**
   * GET /persons/identifier-in-use — checks whether another patient already
   * holds the given (userEnteredId, value) pair. The FE uses this on blur and
   * on save to enforce per-definition uniqueness when the underlying
   * IdentifierDefinition has `allowDuplicates: false`. Always 200; the boolean
   * payload tells the caller whether to block.
   */
  getIdentifierInUse: (
    params: {
      userEnteredId: string;
      value: string;
      excludePersonGuid?: string;
      signal?: AbortSignal;
    },
  ): Promise<IdentifierInUseResponseDto> => {
    const search = new URLSearchParams();
    search.set('userEnteredId', params.userEnteredId);
    search.set('value', params.value);
    if (params.excludePersonGuid !== undefined && params.excludePersonGuid.length > 0) {
      search.set('excludePersonGuid', params.excludePersonGuid);
    }
    return http.get<IdentifierInUseResponseDto>(
      `/persons/identifier-in-use?${search.toString()}`,
      params.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },
};
